import React, { useState, useMemo } from 'react';
import { 
  Scale, Shield, AlertTriangle, CheckCircle2, Lock, 
  Building2, HelpCircle, Mail, MapPin, ChevronRight, 
  ExternalLink, Search, Printer, Share2, FileText, Check,
  Zap, AlertOctagon, RefreshCw, ArrowUpRight, AlertCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { useTheme } from '../../components/ThemeContext';

const TermsAndConditionsPage = () => {
  const { isDarkMode } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [copiedLink, setCopiedLink] = useState(false);

  const keyHighlights = [
    {
      icon: Zap,
      title: "High-Voltage Safety Notice",
      desc: "Software analytics & checklists do NOT replace on-site certified engineer supervision and mandatory physical LOTO isolation.",
      badge: "Critical Notice",
      color: isDarkMode ? "#FBBF24" : "#D97706",
      bg: isDarkMode ? "bg-amber-950/50 border border-amber-800/40" : "bg-amber-50"
    },
    {
      icon: Building2,
      title: "Enterprise Access Control",
      desc: "User accounts are strictly provisioned by authorized corporate admins with role-based access control (RBAC).",
      badge: "Authorized Use",
      color: isDarkMode ? "#38BDF8" : "#1C4D88",
      bg: isDarkMode ? "bg-sky-950/50 border border-sky-800/40" : "bg-blue-50"
    },
    {
      icon: Scale,
      title: "Jurisdiction & Governing Law",
      desc: "This Agreement is governed by the laws of India, subject to exclusive jurisdiction of competent courts in New Delhi.",
      badge: "Legal Venue",
      color: isDarkMode ? "#34D399" : "#059669",
      bg: isDarkMode ? "bg-emerald-950/50 border border-emerald-800/40" : "bg-emerald-50"
    }
  ];

  const sections = [
    {
      id: 'acceptance',
      category: 'general',
      title: '1. Acceptance & Enterprise Agreement',
      icon: Scale,
      content: (
        <div className={`space-y-4 text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
          <p>
            By downloading, installing, accessing, or using the <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>AxonMetric</strong> web portal or mobile applications, you agree to be legally bound by these Terms and Conditions and our Privacy Policy.
          </p>
          <p>
            If you are accessing or using the platform on behalf of an enterprise company (such as a solar power developer, independent power producer, or O&M contractor), you represent that you hold full corporate authority to bind your organization to these terms.
          </p>
        </div>
      )
    },
    {
      id: 'accounts',
      category: 'access',
      title: '2. User Accounts & Credential Safeguards',
      icon: Lock,
      content: (
        <div className={`space-y-4 text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
          <p>
            <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>Enterprise Provisioning:</strong> AxonMetric operates as a restricted enterprise software suite. User accounts are created and configured by authorized system super-administrators and company administrators.
          </p>
          <div className={`p-4 rounded-xl border space-y-2 text-xs ${
            isDarkMode ? 'bg-slate-800/60 border-slate-700/80 text-slate-300' : 'bg-gray-50 border-gray-200 text-gray-600'
          }`}>
            <div className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>User Security Obligations:</div>
            <ul className={`space-y-1.5 list-disc pl-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
              <li>Keep login credentials and biometric unlock devices confidential and secure.</li>
              <li>Immediately notify your organization administrator upon suspected credential compromise.</li>
              <li>Do not transfer, assign, or share JWT tokens or API keys with unauthorized personnel.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'safety-disclaimer',
      category: 'safety',
      title: '3. Industrial Safety & SCADA Operational Disclaimer',
      icon: AlertTriangle,
      content: (
        <div className={`space-y-4 text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
          <div className={`p-5 rounded-2xl border space-y-3 ${
            isDarkMode ? 'bg-amber-950/30 border-amber-800/50' : 'bg-amber-50 border-amber-200'
          }`}>
            <div className={`flex items-center gap-2 font-bold text-sm ${
              isDarkMode ? 'text-amber-300' : 'text-amber-900'
            }`}>
              <AlertOctagon className={`w-5 h-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`} /> High-Voltage Operational Disclaimer
            </div>
            <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-amber-200/90' : 'text-amber-950'}`}>
              AxonMetric provides predictive maintenance insights, daily generation analytics, and digitized O&M checklists. <strong className={isDarkMode ? 'text-amber-100' : 'text-amber-950'}>Under no circumstances does this software replace physical safety checks, electrical danger assessments, or mandatory High-Voltage Lockout/Tagout (LOTO) protocols executed by licensed electrical engineers on-site.</strong>
            </p>
          </div>
          <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
            Plant managers and technicians remain solely responsible for the physical inspection of switchyards, inverters, transformers, and electrical apparatus before commencing physical maintenance.
          </p>
        </div>
      )
    },
    {
      id: 'acceptable-use',
      category: 'access',
      title: '4. Acceptable Conduct & Prohibited Actions',
      icon: Shield,
      content: (
        <div className={`space-y-3 text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
          <p>When interacting with the web portal or mobile application, users agree NOT to:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            {[
              {
                title: "No Geolocation Spoofing",
                desc: "Never falsify or spoof GPS coordinates when recording shift attendance or safety inspection check-ins."
              },
              {
                title: "No Reverse Engineering",
                desc: "Never decompile, disassemble, or reverse-engineer the mobile APK/IPA binaries or REST APIs."
              },
              {
                title: "No False Data Injection",
                desc: "Never deliberately inject fraudulent equipment logs, forged inspection photos, or tampered generation numbers."
              },
              {
                title: "No Infrastructure Probing",
                desc: "Never execute automated penetration tests, vulnerability scanning, or DDoS attacks on AxonMetric servers."
              }
            ].map((item, idx) => (
              <div 
                key={idx} 
                className={`p-3.5 rounded-xl border space-y-1 ${
                  isDarkMode ? 'bg-slate-800/50 border-slate-700/80' : 'border-gray-200 bg-white'
                }`}
              >
                <span className={`font-bold block ${isDarkMode ? 'text-rose-400' : 'text-red-600'}`}>{item.title}</span>
                <p className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: 'intellectual-property',
      category: 'legal',
      title: '5. Intellectual Property Rights',
      icon: FileText,
      content: (
        <div className={`space-y-3 text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
          <p>
            All software code, predictive machine learning models (including RTC power optimization algorithms and inverter failure models), user interfaces, brand assets, and documentation are the exclusive intellectual property of <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>AxonMetric Global Private Limited</strong>.
          </p>
          <p>
            Customer plant telemetry and generation records uploaded to the platform remain the confidential property of the customer enterprise, licensed to AxonMetric strictly for platform execution.
          </p>
        </div>
      )
    },
    {
      id: 'governing-law',
      category: 'legal',
      title: '6. Governing Law & Dispute Resolution',
      icon: Scale,
      content: (
        <div className={`space-y-3 text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
          <p>
            These Terms shall be interpreted and governed in accordance with the laws of the Republic of India. Any legal dispute, controversy, or claim arising out of or relating to this Agreement shall be subject to the exclusive jurisdiction of the competent courts located in <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>New Delhi, India</strong>.
          </p>
          <div className={`p-4 rounded-xl border text-xs ${
            isDarkMode ? 'bg-slate-800/60 border-slate-700 text-slate-300' : 'bg-slate-50 border-slate-200 text-gray-600'
          }`}>
            For inquiries regarding legal notices or enterprise agreements: <a href="mailto:info@axonmetric.com" className={`font-bold underline ${isDarkMode ? 'text-sky-400' : 'text-[#1C4D88]'}`}>info@axonmetric.com</a>.
          </div>
        </div>
      )
    }
  ];

  const filteredSections = useMemo(() => {
    return sections.filter(sec => {
      const matchesCategory = activeTab === 'all' || sec.category === activeTab;
      const matchesSearch = !searchQuery || 
        sec.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sec.id.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [sections, activeTab, searchQuery]);

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(window.location.href);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <Layout header={6} footer={6}>
      <div className={`legal-page min-h-screen font-sans transition-colors duration-200 ${
        isDarkMode ? 'bg-[#0B1120] text-slate-100' : 'bg-[#FAFCFF] text-gray-900'
      }`}>
        {/* Clean Website-Matched Hero Section */}
        <section className={`relative pt-28 pb-16 lg:pt-32 lg:pb-20 overflow-hidden border-b transition-colors duration-200 ${
          isDarkMode 
            ? 'bg-gradient-to-br from-[#0B1120] via-[#0F172A] to-[#0B1120] border-slate-800' 
            : 'bg-gradient-to-br from-white via-gray-50 to-white border-gray-200/70'
        }`}>
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div 
              className={`absolute -top-24 -right-24 w-96 h-96 rounded-full transition-opacity duration-300 ${isDarkMode ? 'opacity-20' : 'opacity-10'}`} 
              style={{ background: isDarkMode ? 'radial-gradient(circle, #38BDF8 0%, transparent 70%)' : 'radial-gradient(circle, #1C4D88 0%, transparent 70%)' }}
            />
            <div 
              className={`absolute -bottom-32 -left-32 w-96 h-96 rounded-full transition-opacity duration-300 ${isDarkMode ? 'opacity-20' : 'opacity-10'}`} 
              style={{ background: isDarkMode ? 'radial-gradient(circle, #0284C7 0%, transparent 70%)' : 'radial-gradient(circle, #1C4D88 0%, transparent 70%)' }}
            />
          </div>

          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="max-w-3xl">
              {/* Badge */}
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6 transition-colors shadow-sm ${
                isDarkMode 
                  ? 'border-slate-700 bg-slate-800/80 text-sky-400' 
                  : 'border-gray-200 bg-white text-gray-900'
              }`}>
                <Scale className="w-4 h-4" style={{ color: isDarkMode ? '#38BDF8' : '#1C4D88' }} />
                <span className={`text-xs sm:text-sm font-extrabold ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                  Enterprise Licensing & Terms of Use
                </span>
              </div>

              {/* Title */}
              <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-5 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Terms and{' '}
                <span className={isDarkMode ? "bg-gradient-to-r from-sky-400 to-blue-400 bg-clip-text text-transparent" : "bg-gradient-to-r from-blue-600 to-[#1C4D88] bg-clip-text text-transparent"}>
                  Conditions
                </span>
              </h1>

              <p className={`text-sm sm:text-base leading-relaxed font-normal mb-8 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                Enterprise operational agreement governing the use of AxonMetric's industrial telemetry suite, mobile monitoring app, and O&M management tools.
              </p>

              {/* Action Toolbar */}
              <div className={`flex flex-wrap items-center gap-3 pt-6 border-t text-xs ${isDarkMode ? 'border-slate-800' : 'border-gray-200'}`}>
                <span className={`font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Effective Date: September 2026</span>
                <span className={`hidden sm:inline ${isDarkMode ? 'text-slate-700' : 'text-gray-300'}`}>•</span>
                <button
                  onClick={() => window.print()}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border font-semibold transition-colors shadow-sm ${
                    isDarkMode 
                      ? 'border-slate-700 bg-slate-800/90 hover:bg-slate-700 text-slate-200' 
                      : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <Printer className={`w-3.5 h-3.5 ${isDarkMode ? 'text-sky-400' : 'text-[#1C4D88]'}`} /> Print Agreement
                </button>
                <button
                  onClick={handleCopyLink}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border font-semibold transition-colors shadow-sm ${
                    isDarkMode 
                      ? 'border-slate-700 bg-slate-800/90 hover:bg-slate-700 text-slate-200' 
                      : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  {copiedLink ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className={`w-3.5 h-3.5 ${isDarkMode ? 'text-sky-400' : 'text-[#1C4D88]'}`} />}
                  {copiedLink ? 'Link Copied!' : 'Share'}
                </button>
                <Link
                  to="/privacy-policy"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold transition-colors shadow-sm border ${
                    isDarkMode 
                      ? 'bg-sky-500/10 border-sky-500/30 text-sky-400 hover:bg-sky-500/20' 
                      : 'bg-blue-50 border-blue-200 text-[#1C4D88] hover:bg-blue-100'
                  }`}
                >
                  View Privacy Policy <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Key Highlights Row */}
        <section className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {keyHighlights.map((hl, idx) => {
              const Icon = hl.icon;
              return (
                <div
                  key={idx}
                  className={`p-5 rounded-2xl border transition-all flex flex-col justify-between ${
                    isDarkMode 
                      ? 'bg-[#111827] border-slate-800 shadow-xl' 
                      : 'bg-white border-gray-200 shadow-sm'
                  }`}
                >
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
                        isDarkMode ? 'bg-slate-800 text-slate-300 border border-slate-700/60' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {hl.badge}
                      </span>
                      <div className={`p-2 rounded-xl ${hl.bg}`}>
                        <Icon className="w-4 h-4" style={{ color: hl.color }} />
                      </div>
                    </div>
                    <h4 className={`font-extrabold text-sm mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{hl.title}</h4>
                    <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{hl.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Search & Tabs */}
        <section className="max-w-7xl mx-auto px-6 mb-8">
          <div className={`border rounded-2xl p-4 flex flex-col md:flex-row items-center justify-between gap-4 transition-colors ${
            isDarkMode 
              ? 'bg-[#111827] border-slate-800 shadow-xl' 
              : 'bg-white border-gray-200 shadow-sm'
          }`}>
            <div className="relative w-full md:w-96">
              <Search className={`w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search clauses (e.g. Safety, Accounts, Prohibited)..."
                className={`w-full pl-10 pr-4 py-2.5 text-xs rounded-xl focus:outline-none focus:ring-2 transition-all ${
                  isDarkMode 
                    ? 'bg-slate-800/80 border border-slate-700 text-white placeholder-slate-400 focus:border-sky-500 focus:ring-sky-500/20' 
                    : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-[#1C4D88]/20 focus:border-[#1C4D88]'
                }`}
              />
            </div>

            <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto">
              {[
                { id: 'all', label: 'All Clauses' },
                { id: 'general', label: 'General' },
                { id: 'access', label: 'Account Rules' },
                { id: 'safety', label: 'Plant Safety' },
                { id: 'legal', label: 'Legal Venue' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? isDarkMode ? 'bg-sky-600 text-white shadow-md shadow-sky-900/30' : 'bg-[#1C4D88] text-white shadow-sm'
                      : isDarkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Clauses List */}
        <section className="max-w-7xl mx-auto px-6 pb-20">
          <div className="space-y-8">
            {filteredSections.length === 0 ? (
              <div className={`rounded-2xl border p-12 text-center space-y-3 shadow-sm ${
                isDarkMode ? 'bg-[#111827] border-slate-800' : 'bg-white border-gray-200'
              }`}>
                <AlertCircle className={`w-10 h-10 mx-auto ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                <h4 className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>No matching clauses found</h4>
                <p className={`text-xs max-w-sm mx-auto ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  Try refining your search keyword or click "All Clauses" to see the full agreement.
                </p>
                <button
                  onClick={() => { setSearchQuery(''); setActiveTab('all'); }}
                  className={`px-4 py-2 text-xs font-bold rounded-xl ${
                    isDarkMode ? 'bg-sky-600 text-white hover:bg-sky-500' : 'bg-[#1C4D88] text-white'
                  }`}
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              filteredSections.map((sec) => {
                const SecIcon = sec.icon;
                return (
                  <div
                    key={sec.id}
                    id={sec.id}
                    className={`border rounded-2xl p-6 sm:p-8 transition-all scroll-mt-24 ${
                      isDarkMode 
                        ? 'bg-[#111827] border-slate-800 text-slate-200 hover:border-sky-500/30 shadow-xl' 
                        : 'bg-white border-gray-200 text-gray-900 hover:border-[#1C4D88]/30 shadow-sm'
                    }`}
                  >
                    <div className={`flex items-center gap-3 mb-5 border-b pb-4 ${isDarkMode ? 'border-slate-800' : 'border-gray-100'}`}>
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm ${
                        isDarkMode ? 'bg-sky-500/10 text-sky-400 border border-sky-500/20' : 'bg-blue-50 text-[#1C4D88]'
                      }`}>
                        <SecIcon className="w-5 h-5" />
                      </div>
                      <h3 className={`text-lg sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{sec.title}</h3>
                    </div>
                    {sec.content}
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default TermsAndConditionsPage;
